import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { cn } from '@/lib/utils';

import type { SlaReport } from '@/types/report.types';

interface SlaChartProps {
  data: SlaReport;
}

interface StatCardProps {
  label: string;
  value: string;
  subtext?: string;
  isGood?: boolean;
}

function StatCard({ label, value, subtext, isGood }: StatCardProps) {
  return (
    <div className='rounded-lg border border-gray-200 p-4'>
      <p className='text-xs font-medium text-gray-500'>{label}</p>
      <p className={cn('text-2xl font-bold mt-1', isGood ? 'text-green-600' : 'text-gray-900')}>{value}</p>
      {subtext && <p className='text-xs text-gray-400 mt-0.5'>{subtext}</p>}
    </div>
  );
}

export function SlaChart({ data }: SlaChartProps) {
  const { t } = useTranslation();
  const { overall } = data;

  const chartData = data.byRole.map((row) => ({
    name: row.category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    avgReview: row.avgHoursToReview,
    avgDecision: row.avgHoursToDecision,
    review48h: row.reviewWithin48hPercent,
  }));

  return (
    <div className='rounded-lg bg-white p-6 shadow'>
      <h3 className='text-lg font-semibold text-gray-900 mb-4'>{t('reports.sla.title')}</h3>

      {/* Stat cards */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-3 mb-6'>
        <StatCard
          label={t('reports.sla.avgToReview')}
          value={`${overall.avgHoursToReview} ${t('reports.sla.hours')}`}
        />
        <StatCard
          label={t('reports.sla.avgToDecision')}
          value={`${overall.avgHoursToDecision} ${t('reports.sla.hours')}`}
        />
        <StatCard
          label={t('reports.sla.reviewWithin48h')}
          value={`${overall.reviewWithin48hPercent}%`}
          isGood={overall.reviewWithin48hPercent >= 80}
        />
        <StatCard
          label={t('reports.sla.decisionWithin72h')}
          value={`${overall.decisionWithin72hPercent}%`}
          isGood={overall.decisionWithin72hPercent >= 80}
        />
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div>
          <h4 className='text-sm font-medium text-gray-700 mb-2'>{t('reports.sla.byRole')}</h4>
          <ResponsiveContainer width='100%' height={250}>
            <BarChart data={chartData} margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis dataKey='name' tick={{ fontSize: 12 }} />
              <YAxis
                tick={{ fontSize: 12 }}
                label={{
                  value: t('reports.sla.hours'),
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 12 },
                }}
              />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey='avgReview' name={t('reports.sla.avgToReview')} fill='#3b82f6' radius={[4, 4, 0, 0]} />
              <Bar dataKey='avgDecision' name={t('reports.sla.avgToDecision')} fill='#8b5cf6' radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Summary stat */}
      <div className='mt-4 text-sm text-gray-500'>
        {t('reports.sla.totalReviewed')}: <span className='font-semibold text-gray-900'>{overall.totalReviewed}</span>
      </div>
    </div>
  );
}
